import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  formatDateWithDayjs,
  sanitizeInput,
  showToast
} from "../../../../../utils/utils";
import { format } from "date-fns";
import { COLUMNS } from "./columns";
import {
  noDataPresent,
  lessBelowHundredReport,
  buttonName,
  fileDownloadMessages,
  checklist,
  SearchText,
  responseStatus
} from "../../../../../locales/en";
import style from "../../../../../assets/scss/custom.module.scss";
import "./../../reportable/reportable.scss";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  usePagination,
  useExpanded
} from "react-table";
import { getLoanLessHundreadThunk } from "../../../../../redux/loanLessHundreadSlice";
import { useDispatch, useSelector } from "react-redux";
import { getLessThan100DownloadURL } from "../../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../../utils/axiosPrivate";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import SearchField from "../../../../common/searchbox/SearchField";
import {
  ddMMMyyyy,
  PAGE_INDEX,
  PAGE_SIZE
} from "../../../../../utils/constants/appConstants";
import ErrorBoundary from "../../../../error-boundary";
import DualPagination from "../../../../common/DualPagination";
import ListViewFallback from "../../../../error-boundary/fallback/ListViewFallback";
import DataCellLoan from "./DataCellLoan";

const LoansLessHundredTable = () => {
  const columns = useMemo(() => COLUMNS, []);
  const initialLoadingBtn = { loginBtn: false };
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [loadingBtn, setLoadingBtn] = useState(initialLoadingBtn);
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.loanLessHundred);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const loadUsers = useCallback(
    async (pageNumber, pageSize) => {
      try {
        const payload = {
          page: pageNumber,
          size: pageSize,
          keyword: sanitizeInput(searchQuery)
        };

        const response = await dispatch(
          getLoanLessHundreadThunk(payload)
        ).unwrap();

        if (response) {
          const responseData = response?.data.data || [];
          const total = response?.data.pageInfo?.totalPages || 0;

          setData(responseData);
          setTotalPages(total);
        }
      } catch {
        setData([]);
        setTotalPages(1);
      }
    },
    [dispatch, searchQuery]
  );

  const [expandedRows, setExpandedRows] = useState([]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },
    useFilters,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns]);
    }
  );

  const { pageIndex } = state;

  const dollarFields = useMemo(() => ["totalAmount"], []);

  const toggleRow = useCallback((rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  }, []);

  // Handle changes in the search input
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") setSearchQuery("");
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        const trimmedQuery = tempSearchQuery.trim();
        gotoPage(0);
        setSearchQuery(trimmedQuery);
      }
    },
    [gotoPage, tempSearchQuery]
  );

  const handleDownload = useCallback(async () => {
    try {
      setLoadingBtn((prev) => ({ ...prev, loginBtn: true }));
      const response = await privateAxios.get(getLessThan100DownloadURL, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const currentDateStr = new Date();
      const currentDate = formatDateWithDayjs(
        currentDateStr.toISOString(),
        "DD-MMM-YYYY"
      );
      link.setAttribute(
        "download",
        `Export_loans_less_than_hundred_${currentDate}.xlsx`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast(checklist?.errors.fileDownload, responseStatus?.success);
    } catch {
      showToast(
        fileDownloadMessages?.errors?.fileDownloadFailed,
        responseStatus?.error
      );
    } finally {
      setLoadingBtn((prev) => ({ ...prev, loginBtn: false }));
    }
  }, []);

  useEffect(() => {
    loadUsers(pageIndex, pageSize);
  }, [loadUsers, pageIndex, pageSize]);

  return (
    <>
      <div className="d-flex flex-wrap mt-3 mb-3 justify-content-sm-between search">
        <ul
          className={`${style.searchOptionWrap} d-flex flex-wrap align-items-center`}
        >
          <li className="my-1 me-1">
            <button onClick={handleDownload} className="btn btn-primary">
              {loadingBtn.loginBtn ? buttonName?.loading : buttonName?.download}
            </button>
          </li>
        </ul>
        <div className={style.inputFilterBar}>
          <div className="my-1 d-flex align-items-center">
            <label className="me-2" htmlFor="">
              {`${lessBelowHundredReport?.filter?.search} `}
            </label>
            <SearchField
              tempSearchQuery={tempSearchQuery}
              handleSearchChange={handleSearchChange}
              handleKeyDown={handleKeyDown}
              SearchText={SearchText}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <div className="col-md-12">
        <ErrorBoundary fallback={<ListViewFallback />}>
          <table
            className={`mt-2 mb-3 reportable_less_hundred bgless_table`}
            {...getTableProps()}
          >
            <thead ref={headerRef}>
              {headerGroups.map((headerGroup) => {
                const { key, ...rest } = headerGroup.getHeaderGroupProps();
                return (
                  <tr className="parentrow" {...rest} key={key}>
                    <th />
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()} key={column.id}>
                        <span>{column.render("Header")}</span>
                      </th>
                    ))}
                  </tr>
                );
              })}
            </thead>

            <tbody {...getTableBodyProps()}>
              {loading && (
                <TableSkeleton rows={10} columns={visibleColumnCount} />
              )}
              {!loading && page.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 2} className="text-center">
                    {noDataPresent}
                  </td>
                </tr>
              )}
              {!loading &&
                page.length > 0 &&
                page.map((row) => {
                  prepareRow(row);
                  const isExpanded = expandedRows.includes(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <tr className="parentrow" {...row.getRowProps()}>
                        <td className="expand-td">
                          <span
                            className="expandbtn cursor-pointer"
                            onClick={() => toggleRow(row.id)}
                          >
                            {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                          </span>
                        </td>
                        {row.cells.map((cell) => (
                          <DataCellLoan
                            key={cell.getCellProps?.().key ?? undefined}
                            cell={cell}
                            column={cell.column}
                            row={cell.row}
                          />
                        ))}
                      </tr>
                      {isExpanded && (
                        <tr className="childrow colexpand">
                          <td colSpan={columns.length + 2}>
                            <div className="tdbody">
                              {columns.map((column) => {
                                const value = row.original[column.accessor];

                                let displayValue;

                                if (
                                  column.accessor === "repaymentDate" &&
                                  value
                                ) {
                                  displayValue = format(
                                    new Date(value),
                                    ddMMMyyyy
                                  );
                                } else if (
                                  dollarFields.includes(column.accessor)
                                ) {
                                  const formattedValue =
                                    value !== undefined &&
                                    value !== null &&
                                    value !== ""
                                      ? Number(value).toFixed(2)
                                      : "0.00";
                                  displayValue = `$${formattedValue}`;
                                } else {
                                  displayValue = String(value ?? "");
                                }

                                return (
                                  <span
                                    key={column.accessor}
                                    data-label={column.Header}
                                  >
                                    {displayValue}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </ErrorBoundary>
      </div>
      {data && data.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}
    </>
  );
};

export default LoansLessHundredTable;
